import { useMemo } from "react";
import { CheckCircle2, Loader2, Sparkles, XCircle } from "lucide-react";
import type { CarouselIdea, CarouselItem } from "@/lib/api";
import type { AiJobParam } from "@/components/AiJobModal";
import LlmReceiveProgress from "@/components/LlmReceiveProgress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { estimateLlmProgress } from "@/lib/llmProgress";
import { useNowTick } from "@/lib/useNowTick";
import { cn } from "@/lib/utils";

function ideaBlurb(idea?: CarouselIdea | null): string {
  if (!idea) return "";
  return String(idea.concept || idea.scene || idea.structure || "").trim();
}

export default function ConceptsJobInline({
  item,
  params,
  running,
  failed,
  statusLabel,
  error,
  abortPending,
  onAbort,
  className,
}: {
  item?: CarouselItem | null;
  params?: AiJobParam[];
  running: boolean;
  failed?: boolean;
  statusLabel?: string;
  error?: string;
  abortPending?: boolean;
  onAbort?: () => void;
  className?: string;
}) {
  const ideas = item?.ideas || [];
  const progress = item?.llm_progress;
  const now = useNowTick(running);
  const live = useMemo(
    () =>
      estimateLlmProgress(
        {
          ...(progress || {}),
          started_at: progress?.started_at || "",
          phase: progress?.phase || (running ? "waiting" : "done"),
        },
        now,
        item?.llm_status,
      ),
    [progress, now, item?.llm_status, running],
  );
  const pct = running ? live.percent : ideas.length >= 4 ? 100 : Math.round((ideas.length / 4) * 100);

  const show = Boolean(running || failed || error);
  if (!show) return null;

  return (
    <section
      id="concepts-job"
      className={cn(
        "overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-b from-violet-50/80 via-white to-white shadow-sm",
        className,
      )}
      aria-live="polite"
    >
      <div className="border-b border-violet-100/80 px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-violet-700/80">
              <Sparkles className="h-3.5 w-3.5" />
              Krok · koncepcje
            </p>
            <h2 className="text-lg font-semibold tracking-tight text-slate-900">
              {running ? "Składam 4 kąty" : failed ? "Nie udało się złożyć koncepcji" : "Koncepcje"}
            </h2>
            <p className="flex items-center gap-2 text-sm text-slate-600">
              {running ? (
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-violet-600" />
              ) : failed ? (
                <XCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
              )}
              <span>{statusLabel || (running ? "Model dobiera haki i kąty…" : failed ? "Błąd" : "Gotowe")}</span>
            </p>
          </div>
          {running && onAbort ? (
            <Button type="button" size="sm" variant="outline" disabled={abortPending} onClick={onAbort}>
              Przerwij
            </Button>
          ) : null}
        </div>

        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between text-xs text-slate-600">
            <span>
              {ideas.length} / 4 kart
              {running ? " · pojawią się poniżej" : ""}
            </span>
            <span className="tabular-nums font-medium text-violet-800">{pct}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-violet-100">
            <div
              className="h-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-500"
              style={{ width: `${Math.max(4, Math.min(100, pct))}%` }}
            />
          </div>
        </div>

        {params?.length ? (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {params.slice(0, 6).map((row) => (
              <span
                key={row.label}
                className="inline-flex max-w-full items-center gap-1 rounded-full border border-violet-200/80 bg-white/80 px-2.5 py-1 text-[11px] text-violet-950"
              >
                <span className="text-violet-600/80">{row.label}</span>
                <span className="truncate font-medium">{row.value || "brak"}</span>
              </span>
            ))}
          </div>
        ) : null}

        {error ? <p className="mt-3 text-sm text-destructive">{error}</p> : null}
      </div>

      <div className="grid gap-3 p-5 sm:grid-cols-2">
        {Array.from({ length: 4 }, (_, i) => {
          const idea = ideas[i];
          const ready = Boolean(idea?.hook);
          return (
            <div
              key={idea?.id || `slot-${i}`}
              className={cn(
                "relative min-h-[7.5rem] overflow-hidden rounded-xl border p-4 transition-all duration-300",
                ready
                  ? "border-violet-200 bg-white shadow-sm"
                  : "border-dashed border-violet-200/70 bg-violet-50/40",
              )}
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-[11px] font-medium uppercase tracking-wide text-violet-700/70">
                  Koncepcja {i + 1}
                </span>
                {ready ? (
                  <Badge variant="outline" className="border-violet-200 text-violet-800">
                    {idea?.format_id || idea?.format_source || "kąt"}
                  </Badge>
                ) : running ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-violet-400" />
                ) : null}
              </div>
              {ready ? (
                <>
                  <p className="text-sm font-semibold leading-snug text-slate-900">{idea?.hook}</p>
                  {ideaBlurb(idea) ? (
                    <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-slate-600">{ideaBlurb(idea)}</p>
                  ) : null}
                </>
              ) : (
                <p className="text-sm text-violet-800/50">
                  {running ? "Czekam na kartę…" : "Puste miejsce"}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {running || progress?.phase ? (
        <details className="border-t border-violet-100 px-5 py-3">
          <summary className="cursor-pointer text-xs font-medium text-slate-600">Szczegóły odbioru</summary>
          <LlmReceiveProgress
            className="mt-2"
            progress={progress}
            llmStatus={item?.llm_status}
            fallbackStartedAt={progress?.started_at}
          />
        </details>
      ) : null}
    </section>
  );
}
